import { useMemo, useState } from "react";
import { Box, Button, IconButton, Popover, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import cloverIcon from "../../assets/img/providers/clover-icon.svg";
import { useToast } from "../../context/toast-context";
import { CloverService } from "../../services/clover-service";

interface CloverMerchantBadgeProps {
  branchId: number;
  merchantId?: string | null;
  branchName: string;
  variant?: "light" | "dark";
  size?: number;
  onUnlinked?: (branchId: number) => void;
}

export function CloverMerchantBadge({
  branchId,
  merchantId,
  branchName,
  variant = "light",
  size = 20,
  onUnlinked,
}: CloverMerchantBadgeProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const cloverService = useMemo(() => new CloverService(), []);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setShowConfirm(false);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setShowConfirm(false);
  };

  const handleCopy = async () => {
    if (!merchantId) return;

    try {
      await navigator.clipboard.writeText(merchantId);
      showToast(t("clipboard"), "success");
    } catch {
      showToast(t("branches.clover.copy-error"), "error");
    }
  };

  const handleUnlink = async () => {
    setIsUnlinking(true);
    try {
      await cloverService.unlinkBranch(
        branchId,
        () => {
          showToast(t("branches.clover.unlink-success"), "success");
          handleClose();
          onUnlinked?.(branchId);
        },
        (errorMessage) => {
          showToast(errorMessage, "error");
        }
      );
    } finally {
      setIsUnlinking(false);
    }
  };

  const isOpen = Boolean(anchorEl);
  const iconFilter =
    variant === "light"
      ? "grayscale(100%) brightness(0) invert(1)"
      : "grayscale(100%) brightness(0.4)";

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        title={t("branches.clover.view-merchant-id")}
        className="inline-flex items-center justify-center shrink-0 rounded-full border border-white/30 p-1 hover:bg-white/10 transition-colors cursor-pointer"
        aria-label={t("branches.clover.view-merchant-id")}
      >
        <img
          src={cloverIcon}
          alt="Clover"
          width={size}
          height={size}
          style={{ filter: iconFilter }}
        />
      </button>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              minWidth: 280,
              maxWidth: 360,
              borderRadius: 2,
            },
          },
        }}
      >
        <Box className="flex flex-col gap-3">
          <Typography variant="subtitle2" color="text.secondary">
            {branchName}
          </Typography>
          <div>
            <Typography variant="caption" color="text.secondary" display="block">
              {t("branches.clover.merchant-id")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mt: 0.5 }}>
              <Typography
                variant="body2"
                sx={{ wordBreak: "break-all", fontFamily: "monospace", flex: 1 }}
              >
                {merchantId || t("branches.clover.merchant-id-unavailable")}
              </Typography>
              {merchantId && (
                <Tooltip title={t("branches.clover.copy-merchant-id")} arrow>
                  <IconButton
                    size="small"
                    onClick={handleCopy}
                    aria-label={t("branches.clover.copy-merchant-id")}
                    sx={{ mt: -0.5, color: "text.secondary" }}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"
                        fill="currentColor"
                      />
                    </svg>
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </div>

          {merchantId && onUnlinked && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 0.5 }}>
              {!showConfirm ? (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => setShowConfirm(true)}
                  sx={{ textTransform: "none" }}
                >
                  {t("branches.clover.unlink")}
                </Button>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {t("branches.clover.unlink-confirm")}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      disabled={isUnlinking}
                      onClick={handleUnlink}
                      sx={{ textTransform: "none", flex: 1 }}
                    >
                      {isUnlinking ? t("loading") : t("branches.clover.unlink-confirm-action")}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={isUnlinking}
                      onClick={() => setShowConfirm(false)}
                      sx={{ textTransform: "none", flex: 1 }}
                    >
                      {t("branches.clover.unlink-cancel")}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
}
